import { useFormContext } from 'react-hook-form';
import { selectUserProject } from 'core/userContext';
import { Workflow } from 'modules/workflowIndex/types';
import {
  createContext,
  createRef,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from 'react';
import { useSelector } from 'react-redux';
import noop from 'utils/noop';
import { getWorkflow } from '../../../../ducks';
import {
  Campaign,
  ExcludedCampaignType,
  ProjectMessageTypesAndSenders,
} from './CloningCampaignsContext.types';
import { getCampaignNodes, getErrorMsg } from './CloningCampaignsContext.utils';
import { getMessageTypesAndSenders } from '../CloningCampaigns/api';

type CloningCampaignsContextState = {
  campaignNodes: Campaign[];
  cloneableCampaigns: Record<number, Campaign>;
  excludedCampaigns: Record<number, ExcludedCampaignType>;
  copyCampaigns: boolean;
  copyWebhooks: boolean;
  areOptionsLoaded: boolean;
  selectedProject?: number;
  setCopyCampaigns: (copy: boolean) => void;
  setCopyWebhooks: (copy: boolean) => void;
  setAreOptionsLoaded: (loaded: boolean) => void;
  setSelectedProject: (projectId: number) => void;
  setWorkflowNameInput: (name: string) => void;
  wfNameInput: string;
  messageTypesAndSenders: ProjectMessageTypesAndSenders;
  containerRef: MutableRefObject<HTMLDivElement | null>;
};

export const defaultContext = {
  campaignNodes: [],
  cloneableCampaigns: {},
  excludedCampaigns: {},
  copyCampaigns: false,
  copyWebhooks: false,

  areOptionsLoaded: false,
  selectedProject: undefined,
  setCopyCampaigns: noop,
  setCopyWebhooks: noop,
  setAreOptionsLoaded: noop,
  setSelectedProject: noop,
  setWorkflowNameInput: noop,
  wfNameInput: '',
  messageTypesAndSenders: {},
  containerRef: createRef<HTMLDivElement | null>(),
};

export const CloningCampaignsContext = createContext<CloningCampaignsContextState>(defaultContext);

type CloningCampaignsContextProps = {
  children: React.ReactNode | React.ReactNode[];
  selectedWorkflow?: Workflow;
};

export const CloningCampaignsContextProvider = ({
  selectedWorkflow,
  children,
}: CloningCampaignsContextProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const workflowFromSelector = useSelector(getWorkflow);
  const workflow = selectedWorkflow || workflowFromSelector;
  const campaignNodes = getCampaignNodes(workflow);
  const [cloneableCampaigns, setCloneableCampaigns] = useState({});
  const [excludedCampaigns, setExcludedCampaigns] = useState({});
  const [areOptionsLoaded, setAreOptionsLoaded] = useState(false);

  const currentProject = useSelector(selectUserProject);
  const defaultInput = `Copy of ${workflow.name}`;
  const [selectedProject, setSelectedProject] = useState(currentProject?.id);
  const [copyCampaigns, setCopyCampaigns] = useState(false);
  const [copyWebhooks, setCopyWebhooks] = useState(false);
  const [wfNameInput, setWorkflowNameInput] = useState(defaultInput);
  const [messageTypesAndSenders, setMessageTypesAndSenders] =
    useState<ProjectMessageTypesAndSenders>({});

  const { reset } = useFormContext();

  const mediums = useMemo(
    () =>
      campaignNodes.reduce((acc: any, campaign: Campaign) => {
        if (acc.indexOf(campaign.messageMedium) < 0) return [...acc, campaign.messageMedium];
        else return acc;
      }, [] as string[]),
    [campaignNodes],
  );

  // Fetch message types and senders when a project is selected
  useEffect(() => {
    // if the selected project changes, the made selections should reset
    setAreOptionsLoaded(false);
    setCopyCampaigns(false);
    setCopyWebhooks(false);
    setCloneableCampaigns({});
    setExcludedCampaigns({});
    reset({});

    if (!mediums.length || !selectedProject) return;

    Promise.allSettled(
      mediums.map((m: string) => getMessageTypesAndSenders(selectedProject, m)),
    ).then((values) => {
      function isSuccessful<T>(
        response: PromiseSettledResult<T>,
      ): response is PromiseFulfilledResult<T> {
        return 'value' in response;
      }

      const fetchedMessageTypesAndSenders: ProjectMessageTypesAndSenders = {};

      campaignNodes.forEach((c) => {
        const { id, messageMedium, name } = c;
        const res = values[mediums.indexOf(messageMedium)];

        if (isSuccessful(res)) {
          const { messageTypes = [], emailSenders = [], smsSenders = [] } = res.value.data;

          setCloneableCampaigns((prev) => ({
            ...prev,
            [id]: c,
          }));

          if (!fetchedMessageTypesAndSenders[messageMedium]) {
            fetchedMessageTypesAndSenders[messageMedium] = {
              messageTypes,
              senders: [...emailSenders, ...smsSenders],
            };
          }
        } else {
          setExcludedCampaigns((prev) => ({
            ...prev,
            [id]: {
              campaignId: id,
              campaignName: name,
              errorMsg: getErrorMsg(res.reason?.response?.data?.message || '', messageMedium),
            },
          }));
        }
      });
      setMessageTypesAndSenders(fetchedMessageTypesAndSenders);
      setAreOptionsLoaded(true);
    });
  }, [selectedProject]);

  const context: CloningCampaignsContextState = {
    campaignNodes,
    cloneableCampaigns,
    excludedCampaigns,
    copyCampaigns,
    copyWebhooks,
    selectedProject,
    areOptionsLoaded,
    messageTypesAndSenders,
    setCopyCampaigns,
    setCopyWebhooks,
    setAreOptionsLoaded,
    setSelectedProject,
    setWorkflowNameInput,
    wfNameInput,
    containerRef,
  };
  return (
    <CloningCampaignsContext.Provider value={context}>{children}</CloningCampaignsContext.Provider>
  );
};

export const useCloningCampaignsContext = () => useContext(CloningCampaignsContext);
